import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Row,
    Col,
    Tag,
    Typography,
    Tabs,
} from 'antd';
import { BsPlusCircle, BsTrash } from 'react-icons/bs';
import {
    useState,
    useEffect,
    useContext,
    FC,
} from 'react';
import { addConcept, updateConcept } from '../../../api/payments';
import { VariableContext } from '../../FinanceEngine/context/VariableContext';
import { OPTIONS } from '../../FinanceEngine/constants/common.constants';
import { MRR_FIELDS, MRR_VARIABLES } from '../../FinanceEngine/constants/mrr.constants';
import { EConceptType } from '../../FinanceEngine/enums/types.enum';

interface Props {
    visible: boolean;
    concept: any;
    onCancel: (concept: any) => void;
}

const { Text } = Typography;
const { TabPane } = Tabs;

const MRRConceptModal: FC<Props> = ({
    visible,
    concept,
    onCancel,
}) => {
    const [form] = Form.useForm<{ operation: string; conditions: any[]; }>();
    const [loading, setLoading] = useState(false);
    const {
        variables,
    } = useContext(VariableContext);
    const operation = Form.useWatch('operation', form);
    const frequency = Form.useWatch('frequency', form);

    const handleOk = () => {
        setLoading(true);
        const values = form.getFieldsValue();
        const conditions = values.conditions.filter((condition: any) => condition?.field && condition?.operator);
        if (concept._id) {
            updateConcept(concept._id, { ...values, conditions, planID: concept.planID })
                .then(({ data }) => {
                    form.resetFields();
                    onCancel(data.data);
                })
                .catch()
                .finally(() => setLoading(false));
        } else {
            addConcept({
                ...values,
                conditions,
                planID: concept.planID,
                type: EConceptType.MRR,
            })
                .then(({ data }) => {
                    form.resetFields();
                    onCancel(data.data);
                })
                .catch()
                .finally(() => setLoading(false));
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel(null);
    };

    useEffect(() => {
        if (!concept) return;
        form.setFieldsValue(concept);
    }, [concept, form]);

    const handleVariableClick = (type: string, variable: any) => {
        const oldOperation = form.getFieldValue('operation') || '';
        let newOperation = `${oldOperation} {{${type}.${variable._id}}}`;
        const input = document.getElementById('operation');
        // @ts-ignore
        const [start, end] = [input.selectionStart, input.selectionEnd];
        if (Number.isInteger(start) && Number.isInteger(end)) {
            newOperation = `${oldOperation.substring(0, start)}{{${type}.${variable._id}}}${oldOperation.substring(end, oldOperation.length)}`;
        }
        form.setFieldsValue({
            operation: newOperation,
        });
    };

    const formatOperation = (operation = '') => {
        const variablesExp = operation.match(/{{variable.(\w+)}}/g);
        let formattedOperation = operation;
        if (variablesExp) {
            variablesExp.forEach((variable) => {
                const id = variable.replace('{{variable.', '').replace('}}', '');
                const variableObj = variables.find((v: any) => v._id === id);
                if (variableObj) {
                    formattedOperation = formattedOperation.replace(variable, `<span style="color: #87d068">${variableObj.name}</span>`);
                }
            });
        }
        const bookingVariablesExp = operation.match(/{{booking.(\w+(?:\.\w+)*)}}/g);
        if (bookingVariablesExp) {
            bookingVariablesExp.forEach((variable) => {
                const id = variable.replace('{{booking.', '').replace('}}', '');
                const variableObj = MRR_VARIABLES.find((v: any) => v._id === id);
                if (variableObj) {
                    formattedOperation = formattedOperation.replace(variable, `<span style="color: #2db7f5">${variableObj.name}</span>`);
                }
            });
        }
        const workspaceVariablesExp = operation.match(/{{workspace.(\w+(?:\.\w+)*)}}/g);
        if (workspaceVariablesExp) {
            workspaceVariablesExp.forEach((variable) => {
                const id = variable.replace('{{workspace.', '').replace('}}', '');
                const variableObj = MRR_VARIABLES.find((v: any) => v._id === id);
                if (variableObj) {
                    formattedOperation = formattedOperation.replace(variable, `<span style="color: #2db7f5">${variableObj.name}</span>`);
                }
            });
        }
        return formattedOperation;
    };

    return (
        <Modal
            title={concept?.name || 'Create MRR Concept'}
            open={visible}
            width={1000}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
                    {concept._id ? 'Update' : 'Create'}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical">

                <Form.Item label="Name" name="name">
                    <Input placeholder="Concept" />
                </Form.Item>

                <Form.Item label="Frequency" name="frequency">
                    <Select placeholder="Select a frequency">
                        <Select.Option value="UNIQUE">Unique</Select.Option>
                        {/* <Select.Option value="WEEKLY">Weekly (7 days)</Select.Option>
                        <Select.Option value="FORTNIGHTLY">Fortnightly (15 days)</Select.Option>
                        <Select.Option value="MONTHLY">Monthly (30 days)</Select.Option> */}
                    </Select>
                </Form.Item>

                <Form.Item label="Conditions">
                    <Form.List name="conditions" initialValue={[]}>
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field) => (
                                    <Row key={field.key} gutter={3}>
                                        <Col span={7}>
                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'field']}
                                                rules={[{ required: true, message: 'Missing field' }]}
                                            >
                                                <Select
                                                    showSearch
                                                    options={MRR_FIELDS}
                                                    placeholder="Field"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={7}>
                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'operator']}
                                                rules={[{ required: true, message: 'Missing operator' }]}

                                            >
                                                <Select
                                                    placeholder="Select a operator"
                                                    options={OPTIONS}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={7}>
                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'value']}
                                                rules={[{ required: true, message: 'Missing value' }]}
                                            >
                                                <Input placeholder="10.0" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={3}>
                                            <Button danger onClick={() => remove(field.name)}>
                                                <span className="anticon">
                                                    <BsTrash size={17} />
                                                </span>
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}

                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                        icon={<span className="anticon"><BsPlusCircle /></span>}
                                    >
                                        Add Condition
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Form.Item>

                <Form.Item label="Operation" name="operation">
                    <Input placeholder="10.0" />
                </Form.Item>

                <Form.Item label="Operation Result">
                    <div
                        dangerouslySetInnerHTML={{ __html: formatOperation(operation) }}
                    />
                </Form.Item>

                <Form.Item label="Variables">
                    <Tabs defaultActiveKey="1">
                        <TabPane tab="Workspace" key="1">
                            <Row justify="start" align="top" gutter={[4, 12]}>
                                {
                                    MRR_VARIABLES.map((variable) => (
                                        <Col key={variable._id}>
                                            <Tag
                                                onClick={() => handleVariableClick('workspace', variable)}
                                                color="#2db7f5"
                                            >
                                                <Text style={{ color: '#1A1F36' }} strong>
                                                    {variable.name}
                                                </Text>
                                            </Tag>
                                        </Col>
                                    ))
                                }
                            </Row>
                        </TabPane>
                        <TabPane tab="Plan Variables" key="2">
                            <Row justify="start" align="top" gutter={[4, 12]}>
                                {
                                    variables.map((variable) => variable.planID && (
                                        <Col key={variable._id}>
                                            <Tag
                                                color="#C9D6E8"
                                                onClick={() => handleVariableClick('variable', variable)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Text style={{ color: '#1A1F36' }} strong>
                                                    {variable.name}
                                                </Text>
                                            </Tag>
                                        </Col>
                                    ))
                                }
                            </Row>
                        </TabPane>
                        <TabPane tab="Global Variables" key="3">
                            <Row justify="start" align="top" gutter={[4, 12]}>
                                {
                                    variables.map((variable) => !variable.planID && variable.origin === EConceptType.MRR && (
                                        <Col key={variable._id}>
                                            <Tag
                                                color="#87d068"
                                                onClick={() => handleVariableClick('variable', variable)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Text style={{ color: '#1A1F36' }} strong>
                                                    {variable.name}
                                                </Text>
                                            </Tag>
                                        </Col>
                                    ))
                                }
                            </Row>
                        </TabPane>
                    </Tabs>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export { MRRConceptModal };
